import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Stack } from '@mui/material'
import {
  getChildCommittees,
  getComAddress,
  type CommitteeBasicInformation,
  type CommitteeAddress,
  type CommitteeBaseInformation,
  type CommitteeJurisdiction, getCommitteeJurisdiction
} from 'services'
import { useParams } from 'react-router-dom'
import { useSessionContext } from 'session'
import {
  CommitteeBasicInfoCard,
  CommitteeAddressesCard,
  CommitteeHierarchyCard,
  JurisdictionCard,
  useGlobalMessageHandler
  , CommitteUrlsCard
} from 'components'
import { createEmptyCommitteeJurisdiction } from 'helpers'

export const CommitteeUpdatePage: React.FC = () => {
  const { comId } = useParams<{ comId: string }>()
  const parsedComId = comId != null ? parseInt(comId, 10) : 1
  const [loading, setLoading] = useState(true)
  const {
    changeModuleName,
    changeUniqueId
  } = useSessionContext()
  const [comBasicInfo, setComBasicInfo] = useState<CommitteeBasicInformation | undefined>(undefined)
  const [comJurisdiction, setComJurisdiction] = useState<CommitteeJurisdiction | undefined>(undefined)
  const [comAddress, setComAddress] = useState<CommitteeAddress[]>([])
  const [childCommittees, setChildCommittees] = useState<CommitteeBaseInformation[]>([])
  const { showErrorMessage, clearMessages } = useGlobalMessageHandler()

  useEffect(() => {
    changeModuleName('Committee')
  }, [changeModuleName])

  useEffect(() => {
    if (comBasicInfo?.id != null) {
      changeUniqueId(`${parsedComId} - ${comBasicInfo.name}`)
      document.title = `Com ${parsedComId} - ${comBasicInfo.name}`
    }
  }, [comBasicInfo?.id])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      clearMessages()
      try {
        setLoading(true)
        const [comAddressesResponse, childCommitteesResponse, comJurisdictionResponse] = await Promise.all([
          getComAddress(parsedComId),
          getChildCommittees(parsedComId),
          getCommitteeJurisdiction(parsedComId)
        ])
        setComBasicInfo(comAddressesResponse)
        setComAddress(comAddressesResponse.addresses)
        setChildCommittees(childCommitteesResponse)
        if (comJurisdictionResponse.succeeded && comJurisdictionResponse.result !== null) {
          setComJurisdiction(comJurisdictionResponse.result)
        } else {
          if (comJurisdictionResponse.status === 404) { setComJurisdiction(createEmptyCommitteeJurisdiction(parsedComId)) }
        }
      } catch (error: any) {
        if (error.response?.status === 404 || error.response?.status === 400) {
          showErrorMessage('Committee Not Found!')
        } else {
          showErrorMessage('Error retrieving committee information')
        }
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [comId])

  return (
    <>
      {loading && <Box display={'flex'} height={'90vh'} flex={1} justifyContent={'center'} alignItems={'center'}><CircularProgress size={100}/></Box>}
      {!loading && comBasicInfo != null && comBasicInfo.id > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1
          }}
        >
          <Stack spacing={2} alignItems={'center'} mt={1} width={'98%'}>
            <CommitteeBasicInfoCard
              originalBasicInfo={comBasicInfo}
              loading={loading}
              updateBasicInfo={setComBasicInfo}
            />
            <CommitteeHierarchyCard loading={loading} childCommittes={childCommittees} committee={comBasicInfo} parentCommitte={comBasicInfo.parent}/>
            {comJurisdiction != null && <JurisdictionCard <CommitteeJurisdiction> loading={loading} originalJurisdiction={comJurisdiction} setOriginalJurisdiction={setComJurisdiction}/>}
            <CommitteeAddressesCard originalAddress={comAddress} originalBasicInfo={comBasicInfo} loading={loading} updateBasicInfo={setComBasicInfo}/>
            <CommitteUrlsCard originalUrls={comBasicInfo.urls} loading={loading} updateBasicInfo={setComBasicInfo} committee={comBasicInfo}/>
          </Stack>
        </Box>
      )
      }
    </>
  )
}
